import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NotificationBarService } from 'src/app/services/notification-bar.service';

interface TextInputeditorOptions {
	name: string;
	label?: string;
	value?: string;
	createField?: boolean;
	buttonText?: string;
	placeholder?: string;
	hint?: string;
	maxLength?: number;
	autocompleteOptions?: { value: any; displayName: string }[];
	callback?: (val: string) => Promise<string | undefined>;
}

@Component({
	selector: 'app-textInputeditor-dialog',
	templateUrl: './textInputeditor-dialog.component.html',
	styleUrls: ['./textInputeditor-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatButtonModule,
		MatProgressSpinnerModule,
		MatAutocompleteModule,
		FlexLayoutModule,
		TranslatePipe,
	],
})
export class TextInputeditorDialogComponent {
	public name: string = 'Text';
	public label: string = 'Text';
	public value: string = '';
	public buttonText: string = 'Update';
	public placeholder: string = '';
	public hint: string = '';
	public autocompleteOptions: { value: any; displayName: string }[] = [];
	public filteredAutocompleteOptions: { value: any; displayName: string }[] = [];
	public maxLength?: number;

	protected inputForm = new FormControl('');

	public isUpdating = false;

	constructor(
		public dialogRef: MatDialogRef<TextInputeditorDialogComponent>,
		private notify: NotificationBarService,
		private translate: TranslateService,
		@Inject(MAT_DIALOG_DATA) public data: TextInputeditorOptions
	) {
		this.name = data.name;
		if (data.label) {
			this.label = data.label;
		} else {
			this.label = data.name;
		}
		if (data.value) {
			this.value = data.value;
			this.inputForm.setValue(data.value);
		}
		if (data.createField) {
			this.buttonText = this.translate.instant('common.create');
		} else {
			this.buttonText = this.translate.instant('common.update');
		}
		if (data.buttonText) {
			this.buttonText = data.buttonText;
		}
		if (data.placeholder) {
			this.placeholder = data.placeholder;
		}
		if (data.hint) {
			this.hint = data.hint;
		}

		this.autocompleteOptions = [...(data.autocompleteOptions || [])];
		this.filteredAutocompleteOptions = [...this.autocompleteOptions];

		if (this.autocompleteOptions) {
			this.inputForm.valueChanges.subscribe((val) => {
				this.filteredAutocompleteOptions = this.autocompleteOptions.filter((option) => option.displayName.includes(val || ''));
			});
		}

		if (this.data.maxLength && this.data.maxLength > 0) {
			this.maxLength = this.data.maxLength;
			this.inputForm.addValidators(Validators.maxLength(this.maxLength));
		}
	}

	readonly autosize = viewChild.required<CdkTextareaAutosize>('autosize');

	public async update() {
		this.isUpdating = true;
		let result: any = undefined;
		const text = this.inputForm.value || '';

		if (this.data.callback) {
			try {
				await this.data.callback(text);
			} catch (err) {
				console.error(err);
				if (err instanceof Error) {
					result = err.message;
				} else if (err instanceof HttpErrorResponse) {
					result = err.error.message || this.translate.instant('dialogs.textInputEditor.serviceUnavailable');
				} else {
					result = err;
				}
			}
		}

		if (result === undefined) {
			this.dialogRef.close(text);
		} else {
			this.notify.notify('error', this.translate.instant('common.error'), result);
		}

		this.isUpdating = false;
	}

	public cancel() {
		this.dialogRef.close();
	}
}
